import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';

import { type UseFormReturn } from 'react-hook-form';
import { ORG_TYPE_OPTIONS } from '@shared';
import { type AdvisorBasicInfoFormValues } from '@client/hooks/useAdvisorOnboardingBasicInfoForm';
import { EdjPilotRestricted } from '@/shared/components/edj-pilot-restricted';
import { LockedField } from '@/shared/components/locked-field';
import { useLockedOrgLabel } from '@/shared/hooks/use-locked-org-label';

interface AdvisorOnboardingBasicInfoStepProps {
  form: UseFormReturn<AdvisorBasicInfoFormValues>;
  onSubmit: () => void;
  isSubmitting: boolean;
  isProfileComplete: boolean;
  isValid: boolean;
  showOrgType: boolean;
  isOrgLocked?: boolean;
}

export function AdvisorOnboardingBasicInfoStep({
  form,
  onSubmit,
  isSubmitting,
  isProfileComplete,
  isValid,
  showOrgType,
  isOrgLocked = false,
}: AdvisorOnboardingBasicInfoStepProps) {
  const organizationDisplayName = form.watch('organizationDisplayName');
  const lockedOrgLabel = useLockedOrgLabel();

  const isDirty = form.formState.isDirty;
  const canAdvance = isDirty || isProfileComplete;

  return (
    <div className="flex flex-col items-center justify-center px-4 sm:px-0">
      <div className="w-full max-w-md space-y-6 sm:max-w-lg sm:space-y-12">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-950 sm:text-3xl">Let's get started</h2>
          <p className="text-foreground mt-2 text-sm sm:text-base">
            Please provide your basic information to set up your account.
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={e => {
              e.preventDefault();
              if (isValid && !isSubmitting && canAdvance) {
                onSubmit();
              }
            }}
            className="space-y-4 sm:space-y-6"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              <FormField
                control={form.control}
                name="advisorFirstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John"
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="advisorLastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Smith"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <LockedField
              active={isOrgLocked}
              tooltipText={`Organization name is preset for ${lockedOrgLabel}.`}
            >
              <FormField
                control={form.control}
                name="organizationDisplayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Organization Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Example Firm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>This will appear on materials your clients see</FormDescription>
                  </FormItem>
                )}
              />
            </LockedField>

            <LockedField
              active={isOrgLocked}
              tooltipText={`Organization legal name is preset for ${lockedOrgLabel}.`}
            >
              <FormField
                control={form.control}
                name="organizationName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Legal Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={organizationDisplayName || 'Example Firm, LLC'}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Your official registered business name (defaults to organization name above)
                    </FormDescription>
                  </FormItem>
                )}
              />
            </LockedField>

            {showOrgType && (
              <EdjPilotRestricted tooltipText='Organization type is preset for the Edward Jones pilot.'>
                <FormField
                  control={form.control}
                  name="orgType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {organizationDisplayName
                          ? `What kind of organization is ${organizationDisplayName}?`
                          : 'What kind of organization is this?'}{' '}
                        <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select
                        value={field.value ?? undefined}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select an organization type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ORG_TYPE_OPTIONS.map(option => (
                            <SelectItem
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </EdjPilotRestricted>
            )}

            <div className="flex flex-col items-center pt-4">
              <Button
                type="submit"
                disabled={!isValid || isSubmitting || !canAdvance}
                className="w-full"
              >
                {isSubmitting ? 'Saving...' : isDirty ? 'Save & Continue' : 'Continue'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
